import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  Typography,
  dialogClasses,
  inputLabelClasses
} from '@mui/material'
import RemoveIcon from '@mui/icons-material/Remove'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import LabelIcon from '@mui/icons-material/Label'
import { makeStyles } from '@mui/styles'
import CheckIcon from '@mui/icons-material/Check'
import { useLocation } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/reducers'
import { useNotifier } from '@cbgms/base/notifier'
import { isValidEmail } from '@cbgms/base/utils/validations'
import { addCustomerEmail, confirmCustomerEmails, fetchCustomerEmails, removeCustomerEmail } from './state/actions'
import DetailsList from './DetailsList'
import { iconButtonClasses } from '@mui/material/IconButton'

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  card: {
    height: 250,
    padding: '5px',
    display: 'flex',
    flexDirection: 'column',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    '-ms-overflow-style': 'none',
    scrollbarWidth: 'none',
    boxShadow: 'none',
    border: '0.1px solid #D4D4D4',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '5px',
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
    overflow: 'scroll'
  },
  emailEntry: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  emailText: {
    flexGrow: 1
  },
  tableContainer: {
    maxHeight: 300
  },
  emailSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #D4D4D4',
    paddingBottom: '5px',
    paddingTop: '5px'
  },
  emailRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 5px',
    backgroundColor: '#f4f4f4'
  },
  emailRowAlt: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 5px',
    backgroundColor: '#FFFFFF'
  },
  headerText: {
    fontWeight: 600,
    fontSize: '10px'
  },
  addButton: {
    color: '#fff',
    boxShadow: 'none'
  },
  dialogTitle: {
    backgroundColor: '#FFFFFF',
    color: '#444'
  },
  yesButton: {
    color: '#fff',
    backgroundColor: '#1976d2',
    '&:hover': {
      backgroundColor: '#1565c0'
    }
  },
  noButton: {
    color: '#fff',
    backgroundColor: '#757575',
    '&:hover': {
      backgroundColor: '#616161'
    }
  },
  requiredLabel: {
    [`& .${inputLabelClasses.asterisk}`]: {
      color: 'red'
    }
  },
  detailsTypography: {
    fontSize: '13px',
    borderLeft: '0.1px solid #D4D4D4',
    borderRight: '0.1px solid #D4D4D4',
    borderTop: '0.1px solid #D4D4D4',
    padding: '10px',
    display: 'inline-block',
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
    fontWeight: '550',
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px'
  },
  emailSectionHeaderTypography: {
    fontWeight: 600,
    fontSize: '15px',
    color: '#666'
  },
  emailSectionHeaderBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  emailSectionHeaderBox30: {
    width: '30%'
  },
  emailSectionHeaderBox10: {
    width: '10%',
    marginLeft: 8
  },
  iconButton: {
    padding: 0,
    borderRadius: '2px',
    color: '#fff',
    width: '18px',
    height: '18px',
    minWidth: '18px',
    minHeight: '18px',
    backgroundColor: '#DB2828',
    '&:hover': {
      backgroundColor: '#DB2828'
    },
    [`&.${iconButtonClasses.disabled}`]: {
      backgroundColor: '#DB2828',
      opacity: 0.5,
      color: '#fff'
    }
  }
}))

const Details: React.FC = () => {
  const notifier = useNotifier()
  const location = useLocation<{ customerId: string; name: string; address: string; custEosId: string; shipToLoc: string }>()
  const { customerId, name, address, custEosId, shipToLoc } = location.state || {
    customerId: '',
    name: '',
    address: '',
    custEosId: '',
    shipToLoc: ''
  }
  let street = ''
  let cityStateZip = ''
  if (address) {
    ;[street, cityStateZip] = address.split(/,(.+)/)
  } else {
    console.error('Address data is not available')
  }
  const classes = useStyles()
  const { t } = useTranslation()
  interface Email {
    Email: string
    Origin: string
    IsPrimary: boolean
  }
  const [emails, setEmails] = useState<Email[]>([])
  const [open, setOpen] = useState(false)
  const [addEmailOpen, setAddEmailOpen] = useState(false)
  const [emailToRemove, setEmailToRemove] = useState<string | null>(null)
  const [newEmail, setNewEmail] = useState('')
  const [IsPrimary, setIsPrimary] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState<boolean>(false)
  const supportURL = useSelector((state: IRootReducerState) => state.auth?.urls?.support)
  const tockenType = useSelector((state: IRootReducerState) => state.auth?.tokens?.TokenType)
  const tockenId = useSelector((state: IRootReducerState) => state.auth?.tokens?.IdToken)
  const App_Id = useSelector((state: IRootReducerState) => state.auth.user.AppId) as number | undefined
  const dispatch = useDispatch()
  useEffect(() => {
    const getEmails = async () => {
      try {
        const emailsData = await dispatch(fetchCustomerEmails(custEosId))
        const emailsString = (emailsData as any).Data.CustomerEmails
        setEmails(JSON.parse(emailsString))
      } catch (error) {
        console.error('Error:', error)
      }
    }
    getEmails()
  }, [custEosId, supportURL, tockenType, tockenId])

  const handleStandardSwitchChange = (emailID: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmails(prevState =>
      prevState.map(item => (item.Email === emailID ? { ...item, IsPrimary: event.target.checked } : { ...item, IsPrimary: false }))
    )
  }

  const handleRemoveEmail = async () => {
    setEmails(prevState => {
      const updatedEmails = prevState.filter(item => item.Email !== emailToRemove)
      setLoading(true)
      const removeEmail = async () => {
        try {
          const emailsData = (await dispatch(removeCustomerEmail(custEosId, updatedEmails, Number(App_Id)))) as any
          setEmails(JSON.parse((emailsData as any).Data.CustomerEmails))
          notifier.success(t('AppLabels.emailRemovedSuccessfully'))
        } catch (error) {
          notifier.error(t('AppLabels.emailRemovalFailure'))
          setLoading(false)
        } finally {
          setLoading(false)
        }
      }
      removeEmail()
      return updatedEmails
    })
  }

  const handleConfirm = async () => {
    setLoading(true)
    try {
      const updatedEmails = await dispatch(confirmCustomerEmails(custEosId, emails, Number(App_Id)))
      setEmails(JSON.parse((updatedEmails as any).Data.CustomerEmails))
      setLoading(false)
      notifier.success(t('AppLabels.standardChangedSuccessfully'))
    } catch (error) {
      notifier.error(t('AppLabels.standardChangeFailure'))
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const handleClickOpen = (emailID: string) => {
    setEmailToRemove(emailID)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEmailToRemove(null)
  }

  const handleConfirmRemove = () => {
    handleRemoveEmail()
    handleClose()
  }

  const handleAddEmailOpen = () => {
    setAddEmailOpen(true)
  }

  const handleAddEmailClose = () => {
    setAddEmailOpen(false)
    setNewEmail('')
    setIsPrimary(false)
    setError('')
  }

  const validateEmail = (email: string): boolean => {
    const validationMessage = isValidEmail(email)
    return validationMessage === undefined
  }

  const handleAddEmail = async () => {
    if (newEmail.trim() === '') {
      setError(t('AppLabels.emailMustBeValid'))
      return
    }

    if (!validateEmail(newEmail)) {
      setError(t('AppLabels.emailMustBeValid'))
      return
    }
    const emailExists = emails.some(email => email.Email === newEmail)
    if (emailExists) {
      notifier.warning(t('AppLabels.emailAlreadyExists'))
      handleAddEmailClose()
      return
    }

    const updatedEmails = emails.map(email => ({
      ...email,
      IsPrimary: IsPrimary ? false : email.IsPrimary
    }))

    const newEmails = [...updatedEmails, { Email: newEmail, Origin: 'Admin-UI', IsPrimary }]

    setLoading(true)
    try {
      const emailsData = await dispatch(addCustomerEmail(custEosId, newEmails, Number(App_Id)))
      setEmails(JSON.parse((emailsData as any).Data.CustomerEmails))
      setLoading(false)
      handleAddEmailClose()
      notifier.success(t('AppLabels.emailAddedSuccess'))
    } catch (error) {
      notifier.error(t('AppLabels.emailAddFailure'))
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  return (
    <div className={classes.root} data-testid='details-component'>
      <Grid container spacing={2} style={{ padding: '10px' }}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} sm={4}>
            <div className={classes.sectionHeader}>
              <Typography variant='h6' className={classes.detailsTypography}>
                {t('AppLabels.details')}
              </Typography>
            </div>

            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={2} direction='column' style={{ padding: '15px' }}>
                  <Typography variant='h6' noWrap>
                    {name}
                  </Typography>

                  <List dense>
                    <ListItem disableGutters style={{ padding: '2px 0' }}>
                      <ListItemIcon>
                        <LocationOnIcon />
                      </ListItemIcon>
                      {street}
                    </ListItem>
                    <ListItem disableGutters style={{ padding: '2px 0' }}>
                      <ListItemText inset>{cityStateZip}</ListItemText>
                    </ListItem>
                    <ListItem disableGutters style={{ padding: '2px 0' }}>
                      <ListItemIcon>
                        <LabelIcon />
                      </ListItemIcon>
                      {t('AppLabels.customerID')}: {customerId}
                    </ListItem>
                    <ListItem disableGutters style={{ padding: '2px 0' }}>
                      <ListItemIcon>
                        <LabelIcon />
                      </ListItemIcon>
                      {t('AppLabels.shipToLoc')}: {shipToLoc}
                    </ListItem>
                  </List>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={8}>
            <div>
              <Typography className={classes.detailsTypography}>{t('AppLabels.emailAddress')}</Typography>
            </div>

            <Card className={classes.card}>
              <Box className={classes.emailSectionHeader}>
                <Box sx={{ width: '50%', display: 'flex', alignItems: 'center' }}>
                  <Button
                    data-testid='add-email-button'
                    color='primary'
                    variant='contained'
                    disabled={emails.length === 0}
                    className={classes.addButton}
                    style={{
                      fontSize: '12px',
                      borderRadius: '3px',
                      height: '30px'
                    }}
                    onClick={handleAddEmailOpen}
                    startIcon={<AddIcon />}
                  >
                    {t('AppLabels.add')}
                  </Button>
                </Box>
                <Box sx={{ width: '50%', display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                  <Button
                    data-testid='confirm-email-button'
                    color='primary'
                    variant='contained'
                    disabled={emails.length === 0}
                    className={classes.addButton}
                    style={{
                      fontSize: '12px',
                      borderRadius: '3px',
                      height: '30px'
                    }}
                    onClick={handleConfirm}
                    startIcon={<CheckIcon />}
                  >
                    {t('AppLabels.confirm')}
                  </Button>
                </Box>
              </Box>

              <Box className={classes.emailSectionHeader}>
                <Box className={`${classes.emailSectionHeaderBox} ${classes.emailSectionHeaderBox30}`}>
                  <Typography className={classes.emailSectionHeaderTypography}>{t('AppLabels.address')}</Typography>
                </Box>
                <Box className={`${classes.emailSectionHeaderBox} ${classes.emailSectionHeaderBox10}`}>
                  <Typography className={classes.emailSectionHeaderTypography}>{t('AppLabels.standard')}</Typography>
                </Box>
                <Box className={`${classes.emailSectionHeaderBox} ${classes.emailSectionHeaderBox30}`}>
                  <Typography className={classes.emailSectionHeaderTypography}>{t('AppLabels.remove')}</Typography>
                </Box>
              </Box>

              <CardContent style={{ padding: '0px', overflow: 'auto' }}>
                {emails &&
                  emails.length > 0 &&
                  emails.map((item, index) => (
                    <Box
                      key={index}
                      className={index % 2 === 0 ? classes.emailRow : classes.emailRowAlt}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Box sx={{ width: '40%', paddingLeft: '5px' }}>
                        <Typography sx={{ fontSize: '1rem', color: '#666' }}>{item.Email}</Typography>
                      </Box>

                      <Box sx={{ width: '30%', display: 'flex', justifyContent: 'center' }}>
                        <Switch
                          checked={item.IsPrimary}
                          onChange={handleStandardSwitchChange(item.Email)}
                          color='primary'
                          disabled={item.IsPrimary}
                        />
                      </Box>

                      <Box sx={{ width: '30%', display: 'flex', justifyContent: 'center' }}>
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => handleClickOpen(item.Email)}
                          disabled={item.IsPrimary}
                          data-testid={`remove-icon-button-${index}`}
                        >
                          <RemoveIcon sx={{ fontSize: '15px' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle style={{ borderBottom: '1px solid #D4D4D4' }} className={classes.dialogTitle}>
            {t('AppLabels.confirmEmailDeletion')}
          </DialogTitle>
          <DialogContent style={{ borderBottom: '1px solid #D4D4D4', paddingTop: '5px' }}>
            <DialogContentText>
              {t('AppLabels.deleteEmailPrompt')} {emailToRemove}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} className={classes.noButton}>
              {t('AppLabels.no')}
            </Button>
            <Button data-testid='yes-button' onClick={handleConfirmRemove} className={classes.yesButton} autoFocus>
              {t('AppLabels.yes')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          data-testid='add-email-modal'
          open={addEmailOpen}
          onClose={handleAddEmailClose}
          sx={{ [`& .${dialogClasses.paper}`]: { width: '500px', height: '350px' } }}
        >
          <DialogTitle style={{ borderBottom: '1px solid #D4D4D4' }} className={classes.dialogTitle}>
            {t('AppLabels.addEmailAddress')}
          </DialogTitle>
          <DialogContent style={{ borderBottom: '1px solid #D4D4D4', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <TextField
                id='outlined-read-only-input'
                label={t('AppLabels.popupEmailAddress')}
                variant='outlined'
                size='small'
                placeholder={t('AppLabels.popupEmailAddress')}
                fullWidth
                value={newEmail}
                onChange={e => {
                  const value = e.target.value
                  setNewEmail(value)
                  if (value === '' || validateEmail(value)) {
                    setError('')
                  } else {
                    setError(t('AppLabels.emailMustBeValid'))
                  }
                }}
                error={!!error}
                helperText={error}
                InputLabelProps={{
                  required: true,
                  classes: {
                    asterisk: classes.requiredLabel
                  }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <TextField
                label={t('AppLabels.origin')}
                type='text'
                fullWidth
                variant='outlined'
                size='small'
                value='Admin-UI'
                placeholder='Origin'
                InputProps={{
                  readOnly: true
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <Typography style={{ marginRight: '10px', width: '150px' }}>{t('AppLabels.standard')}</Typography>
              <Switch checked={IsPrimary} onChange={e => setIsPrimary(e.target.checked)} color='primary' />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button data-testid='close-email-button' onClick={handleAddEmailClose} className={classes.noButton}>
              {t('AppLabels.no')}
            </Button>
            <Button onClick={handleAddEmail} className={classes.yesButton}>
              {t('AppLabels.yes')}
            </Button>
          </DialogActions>
        </Dialog>
        <DetailsList custEosId={custEosId} />
      </Grid>
    </div>
  )
}

export default Details
